import { useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser";
import { useGetCurrentUser } from "../../hooks/useGetUser";
import { LeftMenuItems, RoleEnum, RouteEnum } from "../../models/enums";
import BaseLayout from "./BaseLayout";
import { CC_OPS_ADMIN_LEFT_ITEMS } from "./LeftSideBar/MenuItems/CC_OPS_ADMIN_LEFT_ITEMS";
import { CcOpsItems } from "./LeftSideBar/MenuItems/CcOpsItems";
import { CustomerAdminItems } from "./LeftSideBar/MenuItems/CustomerAdminItems";
import { CustomerOperatorForemanItems } from "./LeftSideBar/MenuItems/CustomerOperatorForemanItems";
import { CustomerOperatorForemanReadOnlyItems } from "./LeftSideBar/MenuItems/CustomerOperatorForemanReadOnlyItems";
import { PartnerAdminItems } from "./LeftSideBar/MenuItems/PartnerAdminItems";
import { PartnerUserItems } from "./LeftSideBar/MenuItems/PartnerUserItems";
import { RootItems } from "./LeftSideBar/MenuItems/RootItems";
import { useGetCustomer } from "../../hooks/useGetCustomer";
import { useGetLocationsCached } from "../../../pages/customer-settings/tabs/locations/hooks/useGetLocationsCached";

export type LeftSideBarItem = {
  name: LeftMenuItems;
  path: RouteEnum;
  nestedItems?: LeftSideBarItem[];
  nonClickable?: boolean;
  showDivider?: boolean;
  isSeparatorOfBottomSection?: boolean;
};

export type LeftSideBarSettingsType = {
  showLeftSideBar: boolean;
  showAllLocationsDropdown: boolean;
  disableClearableInAllLocationsDropdown: boolean;
  hideTagsInAllLocationsDropdown: boolean;
  rememberEmptyValueInAllLocationsDropdown: boolean;
  leftSideBarItems: LeftSideBarItem[];
};

export default function UnifiedLayout() {
  const [isLeftSideBarOpen, setIsLeftSideBarOpen] = useState<boolean>(true);

  const { role } = useAuthenticatedUser();
  const { pathname } = useLocation();

  useGetCustomer();

  useGetLocationsCached();

  useGetCurrentUser();

  const onSetIsLeftSideBarOpen = () => setIsLeftSideBarOpen(prev => !prev);

  let showLeakFinderSettings = false;
  let showCustomerSelect = false;
  let showLeftSideBar = true;
  let showAllLocationsDropdown = true;
  let disableClearableInAllLocationsDropdown = false;
  let hideTagsInAllLocationsDropdown = false;
  let rememberEmptyValueInAllLocationsDropdown = false;
  let leftSideBarItems: LeftSideBarItem[] = [];

  /**
   * Check table with actual roles and permissions here: https://cleanconnect.atlassian.net/wiki/spaces/ENG/pages/133693441/AC+-+Roles+permissions
   */
  switch (role) {
    case RoleEnum.ROOT:
    case RoleEnum.CC_SALES:
      showCustomerSelect = true;

      leftSideBarItems = RootItems;
      break;

    case RoleEnum.PARTNER_ADMIN:
      leftSideBarItems = PartnerAdminItems;

      showCustomerSelect = true;

      break;

    case RoleEnum.CUSTOMER_OPERATOR_FOREMAN:
      leftSideBarItems = CustomerOperatorForemanItems;

      break;

    case RoleEnum.OPERATOR_READ_ONLY:
      leftSideBarItems = CustomerOperatorForemanReadOnlyItems;
      break;

    case RoleEnum.CUSTOMER_ADMIN:
      leftSideBarItems = CustomerAdminItems;

      break;

    case RoleEnum.CC_OPS:
      showCustomerSelect = true;

      leftSideBarItems = CcOpsItems;
      break;

    case RoleEnum.CC_OPS_ADMIN:
      showCustomerSelect = true;

      leftSideBarItems = CC_OPS_ADMIN_LEFT_ITEMS;
      break;

    case RoleEnum.PARTNER_USER:
      leftSideBarItems = PartnerUserItems;

      showCustomerSelect = true;

      break;

    case RoleEnum.LEAKFINDER_DIRECTOR:
      showAllLocationsDropdown = false;

      showLeftSideBar = false;
      break;

    case RoleEnum.CC_PROCTORS:
      showAllLocationsDropdown = false;

      showLeftSideBar = false;

      break;

    case RoleEnum.VERIFIER:
      showAllLocationsDropdown = false;

      showCustomerSelect = false;

      showLeftSideBar = false;

      break;

    default:
      break;
  }

  switch (true) {
    case pathname === RouteEnum.Devices:
      disableClearableInAllLocationsDropdown = true;

      hideTagsInAllLocationsDropdown = true;

      break;

    case pathname === RouteEnum.Nodes:
      disableClearableInAllLocationsDropdown = true;

      hideTagsInAllLocationsDropdown = true;

      break;

    case pathname.includes(RouteEnum.LiveView):
      rememberEmptyValueInAllLocationsDropdown = true;
      break;

    case pathname.includes(RouteEnum.Model):
      rememberEmptyValueInAllLocationsDropdown = true;

      showAllLocationsDropdown = false;

      break;

    case pathname.startsWith(RouteEnum.LeakFinder):
      if (role === RoleEnum.OPERATOR_READ_ONLY) {
        showLeakFinderSettings = false;
      } else {
        showLeakFinderSettings = true;
      }

      showLeftSideBar = false;

      break;

    case pathname.startsWith(RouteEnum.HumanValidator):
    case pathname.startsWith("/media"):
      showCustomerSelect = false;

      showLeftSideBar = false;
      break;
    case pathname.includes(RouteEnum.ZoneListShort):
      showCustomerSelect = false;

      showAllLocationsDropdown = false;

      break;

    case pathname.startsWith(RouteEnum.Unauthorized):
      showLeftSideBar = false;
      break;

    default:
      break;
  }

  const leftSideBarSettings: LeftSideBarSettingsType = {
    showAllLocationsDropdown,
    disableClearableInAllLocationsDropdown,
    hideTagsInAllLocationsDropdown,
    rememberEmptyValueInAllLocationsDropdown,
    leftSideBarItems,
    showLeftSideBar,
  };

  return (
    <BaseLayout
      leftSideBarSettings={leftSideBarSettings}
      open={isLeftSideBarOpen}
      onSetIsLeftSideBarOpen={onSetIsLeftSideBarOpen}
      showLeakFinderSettings={showLeakFinderSettings}
      showCustomerSelect={showCustomerSelect}
    />
  );
}
